if (!customElements.get('aov-offer-presentation')) {
  customElements.define('aov-offer-presentation', class extends HTMLElement {
    connectedCallback() {
      this.root = document.getElementById(this.dataset.productInfo);
      if (!this.root) return;
      try {
        this.config = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (error) {
        return;
      }
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.root, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'aria-checked']
      });
      this.decorate();
      // Currency scripts can arrive after an editor section or app has rendered.
      this.shippingRetries = [500, 1500, 4000].map(delay => setTimeout(() => this.schedule(), delay));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.summary?.remove();
      this.shippingRetries?.forEach(clearTimeout);
      this.root?.querySelectorAll('[data-pets-aov-shipping]').forEach(node => node.remove());
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.decorate();
      });
    }

    decorate() {
      // Observe only this product, and never replace or move app-owned nodes.
      this.root.querySelectorAll('.Avada-Bundle-Volume__Container').forEach(container => {
        if (!container.classList.contains('pets-aov-offers')) container.classList.add('pets-aov-offers');
        Object.entries(this.config.colors).forEach(([name, value]) => {
          const property = `--pets-aov-${name}`;
          if (container.style.getPropertyValue(property) !== value) container.style.setProperty(property, value);
        });
        container.querySelectorAll('.Avada-VolumeItem__Container > .Avada-Volume__Item').forEach((item, index) => {
          const info = item.querySelector('.Avada-Volume__Info');
          if (!info) return;
          const tier = this.config.tiers[index] || {};
          this.setDescription(info, tier.description || '');
          this.setBenefits(info, this.formatBenefits(item, tier.benefits || ''));
          this.setShipping(item, index);
        });
      });
      this.updatePriceSummary();
    }

    formatBenefits(item, text) {
      const currency = this.config.shipping?.currency;
      const price = item.querySelector('.AOV-Offer__DiscountPrice');
      return text.split(/[|\r\n]+/).map(line => {
        if (!line.includes('{unit_price:')) return line;
        const cents = price && window.PetsUpsellOffers?.readMoney(price.textContent, currency);
        if (!Number.isFinite(cents) || cents < 0 || !currency) return '';
        return line.replace(/\{unit_price:(\d+)\}/g, (_, quantity) => {
          if (Number(quantity) < 1) return '';
          return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Math.round(cents / Number(quantity)) / 100);
        });
      }).join('|');
    }

    shippingLabel() {
      const shipping = this.config.shipping;
      const amount = Number(shipping?.amount);
      if (!shipping?.enabled || String(shipping.amount ?? '').trim() === '' || !Number.isFinite(amount) || amount < 0) return null;
      const exact = shipping.country === 'US' && shipping.currency === 'USD' && shipping.baseCurrency === 'USD';
      const storefront = window.Shopify?.currency;
      const rate = shipping.currency === shipping.baseCurrency ? 1
        : storefront?.active === shipping.currency ? Number(storefront.rate) : NaN;
      if (!Number.isFinite(rate) || rate <= 0) {
        return { text: shipping.fallbackText || '', note: '' };
      }
      let formatted;
      try {
        formatted = new Intl.NumberFormat(shipping.locale || 'en', {
          style: 'currency', currency: shipping.currency, currencyDisplay: 'symbol'
        }).format(amount * rate);
      } catch (error) {
        return { text: shipping.fallbackText || '', note: '' };
      }
      const template = (exact ? shipping.exactText : shipping.estimateText) || '';
      return {
        text: template.replaceAll('{amount}', formatted).replaceAll('{currency}', shipping.currency),
        note: exact ? '' : shipping.estimateNote || ''
      };
    }

    setShipping(item, index) {
      let node = item.querySelector('[data-pets-aov-shipping]');
      const shipping = this.config.shipping;
      const priceNote = this.config.tiers[index]?.priceNote?.trim() || '';
      const label = !shipping?.enabled ? null
        : index + 1 === Number(shipping.tier) ? this.shippingLabel()
        : priceNote ? { text: priceNote, note: '' } : null;
      const price = item.querySelector('.Avada-Offer__Price');
      if (!price || !label?.text) { node?.remove(); return; }
      if (!node) {
        node = document.createElement('span');
        node.dataset.petsAovShipping = '';
        node.className = 'pets-aov-shipping';
      }
      if (node.textContent !== label.text) node.textContent = label.text;
      if (node.title !== label.note) node.title = label.note;
      const accessible = label.note ? `${label.text}. ${label.note}` : label.text;
      if (node.getAttribute('aria-label') !== accessible) node.setAttribute('aria-label', accessible);
      node.style.setProperty('--pets-aov-shipping-size', `${Number(shipping.fontSize) || 12}px`);
      node.style.setProperty('--pets-aov-shipping-color', shipping.color || this.config.colors.muted);
      if (node.parentElement !== price) price.append(node);
    }

    updatePriceSummary() {
      const price = this.root.querySelector('.product-page-price');
      const selected = this.root.querySelector('.pets-aov-offers .Avada-Volume__Content[aria-checked="true"]');
      const offer = selected?.closest('.Avada-Volume__Item');
      const title = offer?.querySelector('.Avada-Volume__Info--TriggerQty > span:first-child')?.textContent.trim() || '';
      const badge = offer?.querySelector('.Avada-BadgeText')?.textContent.trim() || '';
      if (this.config.showPriceSummary === false || !price || !title) {
        this.summary?.remove();
        return;
      }
      if (!this.summary) {
        this.summary = document.createElement('div');
        this.summary.className = 'pets-aov-price-summary';
        this.summary.setAttribute('aria-live', 'polite');
        this.summary.setAttribute('aria-atomic', 'true');
        this.summaryTitle = document.createElement('span');
        this.summaryTitle.className = 'pets-aov-price-summary__title';
        this.summaryBadge = document.createElement('span');
        this.summaryBadge.className = 'pets-aov-price-summary__badge';
        this.summary.append(this.summaryTitle, this.summaryBadge);
      }
      Object.entries(this.config.colors).forEach(([name, value]) => {
        const property = `--pets-aov-${name}`;
        if (this.summary.style.getPropertyValue(property) !== value) this.summary.style.setProperty(property, value);
      });
      if (this.summaryTitle.textContent !== title) this.summaryTitle.textContent = title;
      if (this.summaryBadge.textContent !== badge) this.summaryBadge.textContent = badge;
      this.summaryBadge.hidden = !badge || this.config.showPriceBadge === false;
      if (this.summary.parentElement !== price) price.append(this.summary);
    }

    setDescription(info, text) {
      let node = info.querySelector('[data-pets-aov-description]');
      if (!text) { node?.remove(); return; }
      if (!node) {
        node = document.createElement('p');
        node.dataset.petsAovDescription = '';
        node.className = 'pets-aov-description';
        info.append(node);
      }
      if (node.textContent !== text) node.textContent = text;
    }

    setBenefits(info, text) {
      let list = info.querySelector('[data-pets-aov-benefits]');
      const lines = text.split(/[|\r\n]+/).map(line => line.trim()).filter(line => line && line.toLowerCase() !== 'none');
      if (!lines.length) { list?.remove(); return; }
      if (list?.dataset.value === text) return;
      if (!list) {
        list = document.createElement('ul');
        list.dataset.petsAovBenefits = '';
        list.className = 'pets-aov-benefits';
        info.append(list);
      }
      list.dataset.value = text;
      list.replaceChildren(...lines.map(line => {
        const negative = line.startsWith('!');
        const item = document.createElement('li');
        item.className = negative ? 'pets-aov-benefit-negative' : 'pets-aov-benefit-positive';
        const icon = document.createElement('span');
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = negative ? '\u2715' : '\u2713';
        item.append(icon, document.createTextNode(negative ? line.slice(1).trim() : line));
        return item;
      }));
    }
  });
}
